<script>
  import { onMount } from 'svelte';
  import { currentScreen, settings, currentUser, showPremiumModal } from '../store.js';
  import { saveSettings, appSettings } from '../../store.js';
  import { listVideoInputs } from '../../media/camera-service.js';
  import { confirmDialog, toast } from '../../ui/dialogs.js';
  import { APP_VERSION } from '../../app-config.js';
  import { readErrorLog, clearErrorLog } from '../../ui/error-log.js';
  import { OVERLAY_STYLES, DEFAULT_OVERLAY_TITLE } from '../../broadcast/overlay-themes.js';
  import { getRecommendedVideoBitrate } from '../../media/recording-policy.js';
  import { hasProAccess } from '../../entitlements.js';

  let errorLog = readErrorLog();
  let showErrorLog = false;
  async function copyErrorLog() {
    try {
      await navigator.clipboard.writeText(JSON.stringify(errorLog, null, 2));
      toast('Error log copied — WhatsApp/email me paste kar sakte ho.', 'success');
    } catch (_) {
      toast('Copy failed.', 'error');
    }
  }
  function wipeErrorLog() {
    clearErrorLog();
    errorLog = [];
    toast('Error log cleared.', 'success');
  }
  import { getRecordingLoadProfile } from '../../recording-utils.js';

  let updateStatus = '';
  $: loadProfile = getRecordingLoadProfile($settings);

  // Camera source list. Device labels only appear after camera permission has
  // been granted once (e.g. after entering a recording screen); until then the
  // list may be empty and we show a hint.
  let videoDevices = [];
  onMount(async () => { videoDevices = await listVideoInputs(); });

  function persistSettingsChange(nextSettings = $settings) {
    Object.assign(appSettings, nextSettings);
    saveSettings();
  }

  function updateSettings(patch) {
    const nextSettings = { ...$settings, ...patch };
    $settings = nextSettings;
    persistSettingsChange(nextSettings);
  }

  function updateSetting(key, value) {
    updateSettings({ [key]: value });
  }

  function goBack() {
    persistSettingsChange();
    currentScreen.set('home');
  }

  function toggle(key) {
    updateSetting(key, !$settings[key]);
  }

  function recommendedBitrate(resolution = $settings.resolution, fps = $settings.fps) {
    return getRecommendedVideoBitrate(resolution, fps);
  }

  async function syncRecommendedBitrate(resolution = $settings.resolution, fps = $settings.fps) {
    if (resolution === '4k' && $settings.resolution !== '4k') {
      const ok = await confirmDialog("4K recording generates massive files and can cause device overheating. It is highly experimental. Continue?", { title: "4K Warning", danger: true });
      if (!ok) {
        const old = $settings.resolution;
        $settings.resolution = ''; 
        setTimeout(() => updateSettings({ resolution: old }), 0);
        return;
      }
    }
    updateSettings({ resolution, fps, videoBitrate: recommendedBitrate(resolution, fps) });
  }

  function selectFps(fps) {
    syncRecommendedBitrate($settings.resolution, fps);
  }

  function applyProfile(resolution, fps, videoBitrate) {
    updateSettings({ resolution, fps, videoBitrate });
  }

  async function checkForUpdate() {
    persistSettingsChange();
    updateStatus = 'Checking for a new deployment...';
    try {
      if (!('serviceWorker' in navigator)) {
        updateStatus = 'Service worker is not available in this browser.';
        return;
      }
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) await registration.update();
      updateStatus = 'Update check complete. Reloading the app...';
      setTimeout(() => location.reload(), 700);
    } catch (err) {
      updateStatus = `Update check failed: ${err?.message || err}`;
    }
  }

  async function forceRefreshApp() {
    persistSettingsChange();
    if (!(await confirmDialog('Force refresh app code? Match history and settings will be kept.', { title: 'Force refresh' }))) return;
    updateStatus = 'Clearing cached app code...';
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(key => caches.delete(key)));
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(registration => registration.unregister()));
      }
      location.reload();
    } catch (err) {
      updateStatus = `Force refresh failed: ${err?.message || err}`;
    }
  }

  // ── PRO overlay customization ──
  $: isPro = hasProAccess($currentUser);

  function requirePro() {
    if (isPro) return true;
    showPremiumModal.set(true);
    return false;
  }

  // Free users can tap styles to PREVIEW them; saving (i.e. using on real
  // recordings) stays PRO-only.
  let previewStyleId = null;
  $: shownStyleId = previewStyleId || $settings.overlayStyle || 'classic';
  $: previewTheme = OVERLAY_STYLES[shownStyleId] || OVERLAY_STYLES.classic;

  function setOverlayStyle(id) {
    previewStyleId = id;
    if (!isPro) return; // preview-only for free users (no modal spam per tap)
    updateSetting('overlayStyle', id);
  }

  function setOverlayTitle(value) {
    if (!requirePro()) return;
    updateSetting('overlayTitle', value.slice(0, 22));
  }

  function resetOverlayBranding() {
    if (!requirePro()) return;
    updateSettings({ overlayTitle: DEFAULT_OVERLAY_TITLE, overlayLogo: '' });
    toast('Overlay branding reset to FFPL defaults.', 'success');
  }

  function hideOverlayLogo() {
    if (!requirePro()) return;
    updateSetting('overlayLogo', 'off');
    toast('Overlay logo hidden.', 'success');
  }

  async function onLogoFile(event) {
    if (!requirePro()) return;
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast('Please pick an image file.', 'error'); return; }
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      // Downscale to 150x150 so the setting stays tiny in localStorage.
      const img = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Image could not be decoded'));
        el.src = dataUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = 150; canvas.height = 150;
      const cctx = canvas.getContext('2d');
      const scale = Math.min(150 / img.width, 150 / img.height);
      const dw = img.width * scale, dh = img.height * scale;
      cctx.drawImage(img, (150 - dw) / 2, (150 - dh) / 2, dw, dh);
      updateSetting('overlayLogo', canvas.toDataURL('image/png'));
      toast('Custom overlay logo saved!', 'success');
    } catch (err) {
      toast('Logo import failed: ' + (err?.message || err), 'error');
    }
  }

  const resolutions = [
    { id: "720p", label: "HD 720p" },
    { id: "1080p", label: "Full HD 1080p" },
    { id: "1440p", label: "QHD 1440p" },
    { id: "4k", label: "Ultra HD 4K" }
  ];

  const bitrates = [
    { id: 6000000, label: "6 Mbps" },
    { id: 8000000, label: "8 Mbps" },
    { id: 12000000, label: "12 Mbps" },
    { id: 16000000, label: "16 Mbps" },
    { id: 20000000, label: "20 Mbps" },
    { id: 25000000, label: "25 Mbps" },
    { id: 30000000, label: "30 Mbps" },
    { id: 40000000, label: "40 Mbps" }
  ];

</script>

<div class="screen" style="overflow-y: auto; padding-bottom: 60px;">
  <button class="btn btn-ghost" on:click={goBack}>← Back</button>
  <h1 style="margin-top:10px">Settings</h1>

  <!-- General Settings -->
  <div class="card">
    <h3 style="margin-top:0;">General</h3>
    <div class="field">
      <label for="venue-name">Default Venue Name</label>
      <input type="text" id="venue-name" value={$settings.venue || ''} on:input={(event) => updateSetting('venue', event.currentTarget.value)} placeholder="e.g. Rohini Sec 28" />
    </div>

    <div class="field">
      <label for="enable-cloud-sync">Cloud Sync (Offline Mode)</label>
      <div class="toggle-group">
        <button class="btn {$settings.enableCloudSync !== false ? 'active' : ''}" on:click={() => updateSetting('enableCloudSync', true)}>ON</button>
        <button class="btn {$settings.enableCloudSync === false ? 'active' : ''}" on:click={() => updateSetting('enableCloudSync', false)}>OFF</button>
      </div>
      <div class="muted" style="font-size: 11px; margin-top: 4px;">Turn OFF to stop background network requests and save data.</div>
    </div>

    <div class="field">
      <label for="confirm-new">Confirm before New Match</label>
      <div class="toggle-group">
        <button class="btn {$settings.confirmNewMatch ? 'active' : ''}" on:click={() => toggle('confirmNewMatch')}>YES</button>
        <button class="btn {!$settings.confirmNewMatch ? 'active' : ''}" on:click={() => updateSetting('confirmNewMatch', false)}>NO</button>
      </div>
    </div>
    
    <div class="field">
      <label for="overball-stats">Show Over-by-Over Stats</label>
      <div class="toggle-group">
        <button class="btn {$settings.showOverballStats ? 'active' : ''}" on:click={() => toggle('showOverballStats')}>YES</button>
        <button class="btn {!$settings.showOverballStats ? 'active' : ''}" on:click={() => updateSetting('showOverballStats', false)}>NO</button>
      </div>
    </div>

    <div class="field">
      <label for="wagon-wheel-toggle">Shot Direction Selector (Wagon Wheel)</label>
      <div class="toggle-group">
        <button class="btn {$settings.enableShotSelector ? 'active' : ''}" on:click={() => toggle('enableShotSelector')}>YES</button>
        <button class="btn {!$settings.enableShotSelector ? 'active' : ''}" on:click={() => updateSetting('enableShotSelector', false)}>NO</button>
      </div>
      <div class="muted" style="font-size:11px; margin-top:2px;">Prompt 8-zone shot placement on 4, 6, and Wickets.</div>
    </div>
  </div>

  <!-- Broadcast & Highlights -->
  <div class="card" style="margin-top: 16px;">
    <h3 style="margin-top:0;">Broadcast &amp; Highlights</h3>
    {#each [
      ['enablePlayerCards', 'Show Player Cards', 'Show Player Cards on new batter/bowler.'],
      ['enableTextCommentary', 'Show AI Commentary Ticker', 'Show AI Commentary Ticker on scoring screen.'],
      ['aiCommentaryAudioEnabled', 'Play AI Commentary Audio', 'Play generated Sidhu/Shastri Hindi commentary audio.'],
      ['enableAutoHighlights', 'Save Highlights (.json)', 'Save timestamps .json file for Auto Highlights along with video.'],
      ['animParticlesEnabled', 'Particle Animations', 'Confetti, fireworks, and flying stumps on wickets.'],
      ['animTextBannersEnabled', 'Text Banners', 'Duck alerts, Hat-trick alerts, and milestones.'],
      ['showAdvancedAnalytics', 'Advanced Analytics', 'Win Predictor and Momentum meter.'],
      ['enableMatchPhases', 'Match Phase Ticker', 'Powerplay, middle, and death overs indicator.'],
      ['autoBroadcastMoments', 'Auto Broadcast Moments', 'Control automatic lower thirds and over summaries.']
    ] as option (option[0])}
      <div class="field">
        <div class="field-label">{option[1]}</div>
        <div class="toggle-group">
          <button class="btn {$settings[option[0]] !== false ? 'active' : ''}" on:click={() => updateSetting(option[0], true)}>ON</button>
          <button class="btn {$settings[option[0]] === false ? 'active' : ''}" on:click={() => updateSetting(option[0], false)}>OFF</button>
        </div>
        <div class="muted" style="font-size:11px; margin-top:2px;">{option[2]}</div>
      </div>
    {/each}
  </div>

  <!-- Camera & Recording Settings -->
  <div class="card" style="margin-top: 16px;">
    <h3 style="margin-top:0;">Camera & Recording</h3>
    <div class="field">
      <div class="field-label">Quick Profile</div>
      <div class="profile-grid">
        <button class="btn" on:click={() => applyProfile('720p', 30, getRecommendedVideoBitrate('720p', 30))}>Long Match Safe</button>
        <button class="btn" on:click={() => applyProfile('1080p', 30, getRecommendedVideoBitrate('1080p', 30))}>Balanced</button>
        <button class="btn" on:click={() => applyProfile('1440p', 30, getRecommendedVideoBitrate('1440p', 30))}>Quality</button>
        <button class="btn" on:click={() => applyProfile('4k', 30, getRecommendedVideoBitrate('4k', 30))}>Ultra 4K</button>
      </div>
    </div>
    <div class="field">
      <label for="recording-layout">Recording Video Layout</label>
      <div class="toggle-group">
        <button class="btn {$settings.recordingLayout !== 'vertical' ? 'active' : ''}" on:click={() => updateSetting('recordingLayout', 'landscape')}>Landscape (YouTube)</button>
        <button class="btn {$settings.recordingLayout === 'vertical' ? 'active' : ''}" on:click={() => updateSetting('recordingLayout', 'vertical')}>Vertical (Insta Reels)</button>
      </div>
    </div>
    <div class="field">
      <label for="res">Video Resolution</label>
      <select id="res" value={$settings.resolution} on:change={(event) => syncRecommendedBitrate(event.currentTarget.value, $settings.fps)}>
        {#each resolutions as r, i_each (r.id || r.name || i_each)}
          <option value={r.id}>{r.label}</option>
        {/each}
      </select>
    </div>

    <div class="field">
      <label for="cam-source">Camera Source</label>
      <select id="cam-source" value={$settings.cameraDeviceId || ''} on:change={(event) => updateSetting('cameraDeviceId', event.currentTarget.value)}>
        <option value="">Auto (back camera)</option>
        {#each videoDevices as dev, i_each (dev.id || dev.name || i_each)}
          <option value={dev.deviceId}>{dev.label}</option>
        {/each}
      </select>
      <div class="muted" style="font-size:11px">
        {#if videoDevices.length > 1}
          Desktop par USB webcam / capture card (DSLR/GoPro feed) yahan choose karo. Phone par "Auto" hi best hai.
        {:else}
          Ek hi camera dikh raha hai. External webcam/capture card connect karke, ya ek baar recording screen kholne ke baad (permission ke liye) list refresh hogi.
        {/if}
      </div>
    </div>

    <div class="field">
      <label for="fps">Frame Rate (FPS)</label>
      <div class="toggle-group">
        <button class="btn {$settings.fps === 30 ? 'active' : ''}" on:click={() => selectFps(30)}>30 FPS</button>
        <button class="btn {$settings.fps === 60 ? 'active' : ''}" on:click={() => selectFps(60)}>60 FPS</button>
      </div>
    </div>

    <div class="field">
      <label for="bitrate">Video Quality (Bitrate)</label>
      <select id="bitrate" value={$settings.videoBitrate} on:change={(event) => updateSetting('videoBitrate', Number(event.currentTarget.value))}>
        {#each bitrates as b, i_each (b.id || b.name || i_each)}
          <option value={b.id}>{b.label}</option>
        {/each}
      </select>
      <div class="muted" style="font-size:11px">Recommended: {recommendedBitrate() / 1000000} Mbps</div>
    </div>

    <div class="load-profile load-{loadProfile.tone}">
      <div>
        <span class="profile-kicker">Recording load</span>
        <strong>{loadProfile.label}</strong>
      </div>
      <p>{loadProfile.message}</p>
    </div>

    {#each [
      ['colorBoost', 'Broadcast Color Boost'],
      ['autoMatchIntro', 'Auto Match Intro'],
      ['voiceCommentary', 'Dual-Phone Voice Commentary']
    ] as option (option[0])}
      <div class="field">
        <div class="field-label">{option[1]}</div>
        <div class="toggle-group">
          <button class="btn {$settings[option[0]] ? 'active' : ''}" on:click={() => updateSetting(option[0], true)}>ON</button>
          <button class="btn {!$settings[option[0]] ? 'active' : ''}" on:click={() => updateSetting(option[0], false)}>OFF</button>
        </div>
      </div>
    {/each}
    <div class="field">
      <div class="field-label">FPS Protection (Always ON)</div>
      <div class="toggle-group">
        <button class="btn active" disabled>30 FPS FLOOR</button>
      </div>
    </div>
    <div class="muted" style="font-size:11px; margin-top:4px;">
      Android APK/PWA par protection mandatory hai. Unsafe 1440p/4K ko 1080p/30 aur measured critical load ko 720p/30 par sirf current video ke liye rakha jayega. Saved quality choice change nahi hoti.
    </div>
  </div>

  <!-- PRO Overlay Customization -->
  <div class="card" style="margin-top: 16px; border: 1px solid rgba(251,191,36,0.35);">
    <h3 style="margin-top:0; display:flex; align-items:center; gap:8px;">
      🎨 Overlay Customization
      <span style="background:linear-gradient(135deg,#f59e0b,#d97706); color:#fff; font-size:10px; font-weight:900; letter-spacing:1px; padding:3px 8px; border-radius:6px;">PRO</span>
    </h3>
    <p class="muted" style="font-size:12px; margin:0 0 12px 0;">Choose your broadcast look and put your own tournament name &amp; logo on recordings.</p>

    {#if !isPro}
      <button class="btn btn-primary btn-block" style="background:linear-gradient(135deg,#f59e0b,#d97706); border:none; font-weight:800;" on:click={() => showPremiumModal.set(true)}>
        🔓 Unlock with PRO Pass
      </button>
    {/if}

    <div class="field" style="margin-top:10px;">
      <div class="field-label">Overlay Style {isPro ? '' : '(tap to preview — PRO to use)'}</div>
      <div class="style-grid">
        {#each Object.entries(OVERLAY_STYLES) as entry (entry[0])}
          <button class="btn style-tile {shownStyleId === entry[0] ? 'active-style' : ''}" on:click={() => setOverlayStyle(entry[0])}>
            <span class="style-dot" style="background:{entry[1].accent}"></span>
            {entry[1].label}
          </button>
        {/each}
      </div>

      <!-- Live mini-preview of the selected broadcast look -->
      <div class="ov-preview" aria-label="Overlay style preview">
        <div class="ov-ticker">
          <span class="ov-ticker-bar" style="background:{previewTheme.teamPanel ? '#2563eb' : previewTheme.accent}"></span>
          {($settings.overlayTitle || 'FFPL S3').toUpperCase()} &nbsp; TITANS vs CHARGERS
        </div>
        <div class="ov-bar" style="background:linear-gradient({previewTheme.barTop}, {previewTheme.barBottom}); border-radius:{Math.min(previewTheme.radius, 12)}px;">
          {#if previewTheme.topLine}
            <div class="ov-topline" style="background:linear-gradient(90deg, {previewTheme.teamPanel ? '#2563eb' : previewTheme.accent}, #fff, {previewTheme.teamPanel ? '#dc2626' : previewTheme.accent}); border-radius:{Math.min(previewTheme.radius, 12)}px {Math.min(previewTheme.radius, 12)}px 0 0;"></div>
          {/if}
          {#if previewTheme.teamPanel}
            <div class="ov-team" style="background:linear-gradient(135deg, rgba(37,99,235,0.9), rgba(37,99,235,0.4));">TIT</div>
          {/if}
          <div class="ov-score">
            <div class="ov-teamname" style="color:{previewTheme.accent}">TITANS</div>
            <div class="ov-runs">142/3 <span style="color:{previewTheme.accent}; font-size:10px;">(15.2)</span></div>
          </div>
          <div class="ov-players">
            <div style="color:#fff;">▸ ROHIT <b>68*</b> <span style="color:#a1a1aa;">(41)</span></div>
            <div style="color:#a1a1aa;">BUMRAH 2-24 (3.2)</div>
          </div>
          <div class="ov-over">
            <div style="color:#a1a1aa; font-size:7px; font-weight:900;">THIS OVER</div>
            <div class="ov-pills">
              <span style="background:#374151;">1</span>
              <span style="background:#10b981;">4</span>
              <span style="background:#8b5cf6;">6</span>
              <span style="background:#ef4444;">W</span>
            </div>
          </div>
        </div>
        <div class="muted" style="font-size:10px; margin-top:6px; text-align:center;">{previewTheme.label} — aisi dikhegi recording par</div>
      </div>
    </div>

    <div class="field" style="{isPro ? '' : 'opacity:0.5; pointer-events:none;'}">
      <label for="overlay-title">Ticker Text (shown before team names)</label>
      <input type="text" id="overlay-title" maxlength="22" value={$settings.overlayTitle ?? DEFAULT_OVERLAY_TITLE} placeholder="e.g. FFPL S3 / MPL 2026" on:input={(event) => setOverlayTitle(event.currentTarget.value)} />
    </div>

    <div class="field" style="{isPro ? '' : 'opacity:0.5; pointer-events:none;'}">
      <div class="field-label">Watermark Logo</div>
      {#if $settings.overlayLogo && $settings.overlayLogo !== 'off'}
        <img src={$settings.overlayLogo} alt="Custom overlay logo preview" style="width:56px; height:56px; border-radius:8px; border:1px solid #334155; background:#0f172a; object-fit:contain; margin-bottom:8px;" />
      {:else if $settings.overlayLogo === 'off'}
        <div class="muted" style="font-size:12px; margin-bottom:8px;">Logo hidden on recordings.</div>
      {:else}
        <div class="muted" style="font-size:12px; margin-bottom:8px;">Using default FFPL logo.</div>
      {/if}
      <div style="display:flex; gap:8px; flex-wrap:wrap;">
        <label class="btn btn-ghost" style="flex:1; min-width:120px; text-align:center; cursor:pointer; font-size:12px;">
          📤 Upload Logo
          <input type="file" accept="image/*" on:change={onLogoFile} style="display:none;" />
        </label>
        <button class="btn btn-ghost" style="flex:1; min-width:100px; font-size:12px;" on:click={hideOverlayLogo}>🚫 Hide Logo</button>
        <button class="btn btn-ghost" style="flex:1; min-width:100px; font-size:12px;" on:click={resetOverlayBranding}>↺ FFPL Default</button>
      </div>
    </div>
  </div>

  <!-- Audio Settings -->
  <div class="card" style="margin-top: 16px;">
    <h3 style="margin-top:0;">Audio (Mic)</h3>
    <div class="field">
      <label for="mic-gain">Microphone Boost (x{$settings.micGain || 1})</label>
      <input type="range" id="mic-gain" min="0" max="10" step="0.1" value={$settings.micGain || 1} on:input={(event) => updateSetting('micGain', Number(event.currentTarget.value))} style="width: 100%;" />
      <div class="muted" style="font-size: 12px; margin-top: 4px;">Slide to increase camera microphone volume.</div>
    </div>

    {#if $settings.voiceCommentary}
      <div class="field">
        <label for="scorer-mic-gain">Scorer Phone Commentary Boost (x{$settings.scorerMicGain || 1})</label>
        <input type="range" id="scorer-mic-gain" min="0" max="5" step="0.1" value={$settings.scorerMicGain || 1} on:input={(event) => updateSetting('scorerMicGain', Number(event.currentTarget.value))} style="width:100%" />
      </div>
    {/if}

    <div class="field">
      <label for="audio-bitrate">Recorded Audio Quality</label>
      <select id="audio-bitrate" value={$settings.audioBitrate} on:change={(event) => updateSetting('audioBitrate', Number(event.currentTarget.value))}>
        <option value={128000}>128 kbps</option>
        <option value={192000}>192 kbps</option>
        <option value={256000}>256 kbps</option>
      </select>
    </div>

    <div class="field" style="margin-top: 16px;">
      <label for="sfx">Master SFX Switch</label>
      <div class="toggle-group">
        <button class="btn {$settings.sfxEnabled ? 'active' : ''}" on:click={() => toggle('sfxEnabled')}>ON</button>
        <button class="btn {!$settings.sfxEnabled ? 'active' : ''}" on:click={() => updateSetting('sfxEnabled', false)}>OFF</button>
      </div>
      <div class="muted" style="font-size:11px; margin-top:2px;">Turn all sound effects on or off globally.</div>
    </div>

    {#each [
      ['sfxHornEnabled', 'Airhorn & Siren', 'Play IPL airhorn and wailing sirens.'],
      ['sfxWicketEnabled', 'Wicket Stinger', 'Play descending reverb tail on wickets.'],
      ['sfxBoundaryEnabled', 'Boundary Cheers & Dot Clicks', 'Play cheers on 4/6 and clicks on dot balls.']
    ] as option (option[0])}
      <div class="field">
        <div class="field-label">{option[1]}</div>
        <div class="toggle-group">
          <button class="btn {$settings[option[0]] !== false ? 'active' : ''}" on:click={() => updateSetting(option[0], true)}>ON</button>
          <button class="btn {$settings[option[0]] === false ? 'active' : ''}" on:click={() => updateSetting(option[0], false)}>OFF</button>
        </div>
        <div class="muted" style="font-size:11px; margin-top:2px;">{option[2]}</div>
      </div>
    {/each}
  </div>

  <div class="card" style="margin-top:16px">
    <h3 style="margin-top:0">App Updates</h3>
    <p class="muted" style="font-size:12px">Check normally first. Force Refresh only clears cached app code; matches and settings stay saved.</p>
    <button class="btn btn-primary btn-block" on:click={checkForUpdate}>Check for Update</button>
    <button class="btn btn-ghost btn-block" style="margin-top:8px" on:click={forceRefreshApp}>Force Refresh App</button>
    {#if updateStatus}<div class="muted" style="font-size:11px; margin-top:8px">{updateStatus}</div>{/if}
  </div>

  <div class="card" style="margin-top:16px">
    <h3 style="margin-top:0">Diagnostics</h3>
    <p class="muted" style="font-size:12px; margin:0 0 10px 0;">
      {errorLog.length === 0 ? 'No errors recorded — app healthy hai. 💚' : `${errorLog.length} error(s) recorded. App me dikkat aaye to ye log copy karke bhejo.`}
    </p>
    {#if errorLog.length > 0}
      <div style="display:flex; gap:8px;">
        <button class="btn btn-ghost" style="flex:1; font-size:12px;" on:click={() => showErrorLog = !showErrorLog}>{showErrorLog ? 'Hide' : 'View'} Log</button>
        <button class="btn btn-ghost" style="flex:1; font-size:12px;" on:click={copyErrorLog}>Copy Log</button>
        <button class="btn btn-ghost" style="flex:1; font-size:12px; color:#f87171; border-color:#7f1d1d;" on:click={wipeErrorLog}>Clear</button>
      </div>
      {#if showErrorLog}
        <div style="margin-top:10px; max-height:220px; overflow:auto; background:#0a0f1a; border:1px solid #1e293b; border-radius:8px; padding:10px; font-family:monospace; font-size:10px; line-height:1.5; user-select:text; -webkit-user-select:text;">
          {#each errorLog.slice().reverse() as e, i_each (e.id || e.name || i_each)}
            <div style="margin-bottom:8px; border-bottom:1px dashed #1e293b; padding-bottom:6px;">
              <div style="color:#fbbf24;">{e.at} · {e.kind} · #{e.screen}</div>
              <div style="color:#fca5a5;">{e.message}</div>
            </div>
          {/each}
        </div>
      {/if}
    {/if}
  </div>

  <div class="card" style="margin-top:16px">
    <h3 style="margin-top:0">About &amp; Legal</h3>
    <p class="muted" style="font-size:12px; margin:0 0 10px 0;">FFPL Cricket Scorer v{APP_VERSION}</p>
    <div style="display:flex; flex-direction:column; gap:8px;">
      <a class="btn btn-ghost btn-block" style="display:flex; align-items:center; justify-content:center; text-decoration:none; margin:0;" href="/privacy-policy.html" target="_blank" rel="noopener">📜 Privacy Policy</a>
      <a class="btn btn-ghost btn-block" style="display:flex; align-items:center; justify-content:center; text-decoration:none; margin:0;" href="/terms.html" target="_blank" rel="noopener">⚖️ Terms &amp; Conditions</a>
      <a class="btn btn-ghost btn-block" style="display:flex; align-items:center; justify-content:center; text-decoration:none; margin:0;" href="/refund-policy.html" target="_blank" rel="noopener">💳 Refund &amp; Cancellation Policy</a>
    </div>
  </div>
</div>

<style>
  .profile-grid { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:7px; }
  .style-grid { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:7px; }
  /* Overlay style live preview — mimics the broadcast lower-third over "grass" */
  .ov-preview {
    margin-top: 10px;
    padding: 12px 10px 8px;
    border-radius: 10px;
    background: linear-gradient(160deg, #14532d 0%, #166534 45%, #14532d 100%);
    border: 1px solid #1e293b;
  }
  .ov-ticker {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: rgba(0,0,0,0.65);
    color: #fff;
    font-size: 8px;
    font-weight: 900;
    letter-spacing: 0.4px;
    padding: 4px 8px 4px 4px;
    border-radius: 4px;
    margin-bottom: 14px;
  }
  .ov-ticker-bar { width: 3px; height: 12px; border-radius: 2px; display: inline-block; }
  .ov-bar {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    box-shadow: 0 4px 10px rgba(0,0,0,0.45);
  }
  .ov-topline { position: absolute; top: 0; left: 0; right: 0; height: 2px; }
  .ov-team {
    width: 30px; height: 30px;
    border-radius: 5px;
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 8px; font-weight: 900;
    flex: 0 0 auto;
  }
  .ov-score { flex: 0 0 auto; min-width: 64px; }
  .ov-teamname { font-size: 7px; font-weight: 900; letter-spacing: 0.5px; }
  .ov-runs { color: #fff; font-size: 15px; font-weight: 900; font-variant-numeric: tabular-nums; line-height: 1.1; }
  .ov-players { flex: 1; font-size: 8px; font-weight: 700; line-height: 1.6; border-left: 1px solid rgba(255,255,255,0.1); padding-left: 8px; min-width: 0; white-space: nowrap; overflow: hidden; }
  .ov-over { flex: 0 0 auto; text-align: right; }
  .ov-pills { display: flex; gap: 3px; margin-top: 3px; }
  .ov-pills span {
    width: 13px; height: 13px;
    border-radius: 50%;
    color: #fff; font-size: 7px; font-weight: 900;
    display: inline-flex; align-items: center; justify-content: center;
  }
  .style-tile { display:flex; align-items:center; gap:8px; font-size:11px; font-weight:800; text-align:left; padding:10px; }
  .style-dot { width:12px; height:12px; border-radius:50%; flex:0 0 auto; box-shadow:0 0 6px rgba(255,255,255,0.25); }
  .active-style { border:1px solid #fbbf24 !important; background:rgba(251,191,36,0.14) !important; }
  .load-profile {
    margin: 12px 0 4px;
    padding: 12px;
    border-radius: 14px;
    border: 1px solid rgba(251,191,36,0.28);
    background:
      radial-gradient(circle at 0% 0%, rgba(251,191,36,0.22), transparent 36%),
      linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.94));
  }
  .load-profile div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  .load-profile strong {
    color: #fbbf24;
    font-size: 12px;
    letter-spacing: 0.08em;
  }
  .profile-kicker {
    color: #cbd5e1;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 900;
  }
  .load-profile p {
    margin: 7px 0 0;
    color: #e5e7eb;
    font-size: 12px;
    line-height: 1.35;
  }
  .load-danger {
    border-color: rgba(248,113,113,0.45);
    background:
      radial-gradient(circle at 0% 0%, rgba(248,113,113,0.28), transparent 36%),
      linear-gradient(135deg, rgba(30,10,20,0.96), rgba(15,23,42,0.94));
  }
  .load-danger strong { color: #f87171; }
  .load-warn strong { color: #fbbf24; }
  .load-good strong { color: #86efac; }
</style>
